import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { AnyAction, Dispatch } from 'redux';
import axios from 'axios/axios-base';
import { ApplicationState } from 'store/rootReducer';
import * as actionTypes from 'constants/action-types';
import { action } from 'typesafe-actions';
import { TransactionListState } from 'store/transactions/transactionsTypes';
import { BlazeMeterApiResponse } from 'types/rest/blazeMeterApiResponse';
import { hideToast, showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import {
  BulkOperation,
  BulkOperationType,
  Service,
  Transaction,
  TransactionCriteria,
  TransactionTransferObjectWrapper,
  TransactionType,
  TransactionWrapper,
} from 'types/rest/assetCatalogTypes';
import { buildPageableQueryParameters, buildSortingParam } from 'utils/pagebleUtil';
import { AxiosResponse } from 'axios';
import MixpanelService from 'utils/mixpanelService';
import {
  CLONE_TRANSACTION,
  CLOSE_BLANK_TRANSACTION,
  CREATE_BLANK_TRANSACTION,
  CREATE_TRANSACTION,
  DELETE_TRANSACTION,
  TRANSACTION_BULK_OPERATION,
  UPDATE_TRANSACTION,
} from 'utils/mixpanelEvents';
import _ from 'lodash';
import i18n from 'i18n';
import { UiState } from 'store/ui/uiReducer';
import { generateDate } from 'utils/dateUtil';
import { MockService, TRANSACTIONAL } from 'types/rest/mockServiceTypes';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import store from 'store/configureStore';
import { createDraft } from 'store/mockServices/mockServiceList/mockServiceListActions';
import {
  reloadTransactionShuttle,
  updateMockServiceTransactionAction,
  updateTransactionPriority,
} from 'store/mockServices/mockServiceDetail/mockServiceDetailActions';
import { swapMockServiceTemplateAction } from 'store/mockServiceTemplates/mockServiceTemplatesActions';
import { reloadTransactionsForTemplate } from 'store/mockServiceTemplates/mockServiceTemplateDetail/mockServiceTemplateDetailActions';
import { ErrorFields } from 'types/rest/dslTypes';
import { createMockServiceThunk } from '../mockServices/mockServiceActions';
import { formattedTextThreeArgs } from 'utils/commonUtil';
import { getErrorMessage, isTemplate } from '../../utils/miscUtil';
import { getActiveWorkspaceId, isPrivateCloud } from '../../utils/bzmUserConfigUtil';
import { postValidateTransactionForBlazeData } from '../../utils/transactionUtils';
import { SelectAllType } from '../../components/blazeTable/BlazeTable.types';
import { EditorTabType } from '../../views/AssetCatalog/Transactions/TransactionEditor/TransactionEditor';
import { SortConfiguration } from '../../types/rest/paginationTypes';
import acApi from '../../axios/ac/acApi';

export const selectTransactions = (ids: number[], selectAllType?: SelectAllType) =>
  action(actionTypes.SELECT_TRANSACTIONS, {
    selectedIds: ids,
    selectAllType: selectAllType,
  });

export const setFilterThunk = (
  serviceId: number,
  searchTerm: string,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return (dispatch: Dispatch) => {
    dispatch(
      action(actionTypes.TRANSACTION_FILTER_CHANGED, {
        serviceId: serviceId,
        searchTerm: searchTerm,
      }),
    );
  };
};

export const blazeExtension = '.txs.json';

export const loadingTransactionsThunk = (loadingText: string): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return (dispatch: Dispatch) => {
    dispatch(action(actionTypes.LOADING_TRANSACTION_LIST, { loadingText: loadingText }));
  };
};

/**
 * Triggers bulk tag action.
 * @param transactionIds - array of transaction ids to tag
 * @param tags - array of tag names to use
 * @param selectAll - type of current selectAll option. If selectAll is set to all items, the criteria object is build to tag
 * transactions matching the current filter.
 */
export const bulkTagThunk = (
  transactionIds: number[],
  tags: string[],
  selectAll?: SelectAllType,
): ThunkAction<any, any, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    try {
      dispatch(loadingTransactionsThunk(i18n.t('transactions:actions.applyingTagsToTxns')));

      const bulkTagAction: BulkOperation = buildBulkOperationBaseDto('TAG', transactionIds, selectAll);
      //Assign tags to base DTO
      bulkTagAction.tags = tags;

      const response = await axios.patch<BlazeMeterApiResponse>(
        `/workspaces/{workspaceId}/transactions`,
        bulkTagAction,
      );
      if (response.data.error) {
        displayErrorForBulkOpsThunk(response, i18n.t('transactions:actions.cantApplyTagsToTxns'), dispatch);
      }

      //Track Mixpanel event
      MixpanelService.track(TRANSACTION_BULK_OPERATION('tag', tags.length));

      const txnLength = selectAll === 'ALL' ? i18n.t('transactions:actions.all') : transactionIds.length;
      return dispatch(getTransactions(`${txnLength} ${i18n.t('transactions:actions.appliedTagsToTxns')}`));
    } catch (e) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('transactions:actions.failedToApplyTagsToTxns')} ${
            e.response ? e.response.data.error : 'Something went wrong.'
          }`,
        ),
      );
      return dispatch(getTransactions());
    }
  };
};

export const openTransactionBulkTagModalAction = () => action(actionTypes.OPEN_TRANSACTION_BULK_TAG_MODAL, {});
export const closeTransactionBulkTagModalAction = () => action(actionTypes.CLOSE_TRANSACTION_BULK_TAG_MODAL, {});
export const editorTabChanged = (tab: EditorTabType) => action(actionTypes.CHANGE_EDITOR_TAB, tab);
export const sortTransactionsAction = (sort: SortConfiguration) => action(actionTypes.SORT_TXN_TABLE, sort);

/**
 * Creates transaction criteria with Spring Filter.
 */
const buildTransactionCriteria = (): TransactionCriteria | undefined => {
  //Build filter based on the user ui settings (service picker & search)
  const searchValue = (store.getState() as ApplicationState).transactions.currentSearchTerm;
  const selectedService = (store.getState() as ApplicationState).ui.pageFilter.serviceId;

  // Search is active if there is some value in it
  const isSearchActive = !_.isNil(searchValue) && !_.isEmpty(searchValue);

  // Service is selected if there is some positive number in the selection representing serviceId
  const isSelectedService = selectedService > 0;

  const transactionCriteria: TransactionCriteria = {
    serviceId: isSelectedService ? selectedService : undefined,
    keyword: isSearchActive ? searchValue : undefined,
  };

  if (isSelectedService || isSearchActive) {
    return transactionCriteria;
  }

  return undefined;
};

/**
 * Prepares bulk operation DTO based on selected ids or if select all is used, we need to create
 * criteria object based on current user selection (search filter, service filter) etc. to perform
 * bulk operation on items user is interested in.
 *
 * @param actionType
 * @param transactionIds
 * @param selectAll
 */
export const buildBulkOperationBaseDto = (
  actionType: BulkOperationType,
  transactionIds: number[],
  selectAll?: SelectAllType,
): BulkOperation => {
  if (selectAll !== 'ALL') {
    return {
      action: actionType,
      ids: transactionIds,
    };
  }

  //Select All is used
  const transactionCriteria = buildTransactionCriteria();
  const bulkOperationDto: BulkOperation = {
    action: actionType,
    ids: transactionCriteria ? undefined : [-1],
    specificationCriteria: transactionCriteria ? JSON.stringify(transactionCriteria) : undefined,
  };

  return bulkOperationDto;
};

export const bulkDeleteThunk = (
  transactionIds: number[],
  selectAll?: SelectAllType,
): ThunkAction<any, any, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    //Track Mixpanel event
    MixpanelService.track(TRANSACTION_BULK_OPERATION('delete', transactionIds.length));

    try {
      dispatch(loadingTransactionsThunk(i18n.t('transactions:actions.deletingTxns')));
      const bulkDto = buildBulkOperationBaseDto('DELETE', transactionIds, selectAll);
      const response = await axios.patch<BlazeMeterApiResponse>(`/workspaces/{workspaceId}/transactions`, bulkDto);
      if (response.data.error) {
        displayErrorForBulkOpsThunk(response, i18n.t('transactions:actions.failedToDeleteTxns'), dispatch);
      } else {
        const txText =
          transactionIds.length > 0
            ? transactionIds.length + i18n.t('transactions:actions.leadingSpaceTxns')
            : transactionIds.length + i18n.t('transactions:actions.leadingSpaceTxn');
        return dispatch(getTransactions(`${i18n.t('transactions:actions.successfullyDeleted')} ${txText}`));
      }
    } catch (e) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('transactions:actions.failedToDeleteSelectedTxns')}${
            e.response ? e.response.data.error : 'Something went wrong.'
          }`,
        ),
      );
    } finally {
      //Clear txn selection in redux store
      dispatch(selectTransactions([], undefined));
    }
  };
};

export const recoverTransactionsThunk = (transactionIds: number[]): ThunkAction<any, any, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    //Track Mixpanel event
    MixpanelService.track(TRANSACTION_BULK_OPERATION('restore', transactionIds.length));
    try {
      const response = await acApi.transactions.restoreTransactions(transactionIds);
      if (response.data.error) {
        dispatch(
          showToastNotification(
            ToastType.Danger,
            `${i18n.t('transactions:actions.failedToRecoverSelectedTxns')}`,
            response.data.error,
          ),
        );
      } else {
        dispatch(
          showToastNotification(
            ToastType.Success,
            `${i18n.t('transactions:actions.restoreSuccessful')}`,
            `Successfully restored ${_.size(transactionIds)} ${
              _.size(transactionIds) == 1 ? 'transaction' : 'transactions'
            }.`,
          ),
        );
      }
    } catch (e) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('transactions:actions.failedToRecoverSelectedTxns')}`,
          getErrorMessage(e),
        ),
      );
    }
  };
};

export const displayErrorForBulkOpsThunk = (
  response: AxiosResponse<BlazeMeterApiResponse>,
  errorMsg: string,
  dispatch: ThunkDispatch<any, any, AnyAction>,
) => {
  let detailError = '';
  if (response.data.result && response.data.result.length > 0) {
    detailError = response.data.result[0].message;
  }
  dispatch(showToastNotification(ToastType.Danger, `${errorMsg}. Reason: ${response.data.error}. ${detailError}`));
};

export const getTransactions = (postTxLoadMessage?: string): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    const transactionsConfig: TransactionListState = getState().transactions;
    const uiConfig: UiState = getState().ui;
    const searchTerm: string = encodeURIComponent(uiConfig.pageFilter.searchTerm);

    // Make sure IDs that are 0 are treated as empty strings for query purposes
    const [serviceId, mockId] = [uiConfig.pageFilter.serviceId, transactionsConfig.activeServiceMock].map(
      (id: number) => {
        return !!id ? id.toString() : '';
      },
    );

    // Build the request for transactions, optionally filtered by:
    const pageParams = buildPageableQueryParameters(transactionsConfig.pageable);
    const sortParam = buildSortingParam(getState().transactions.sort);
    const params = {
      ...pageParams,
      serviceId: serviceId,
      serviceMockId: mockId,
      filter: searchTerm,
      type: uiConfig.pageFilter.txType,
      sort: !_.isNil(sortParam) ? sortParam : '-id',
    };

    dispatch({
      type: actionTypes.LOADING_TRANSACTION_LIST,
      payload: { loadingText: i18n.t('transactions:actions.loadingTxns') },
    });
    try {
      // Make call to fetch transactions from the API
      const response = await acApi.transactions.getTransactions({ params: params });

      // If we have zero results from backend and there are some results available in total, and we are not at first
      // page, go to previous page
      if (response.data.result.length === 0 && response.data.total !== 0 && transactionsConfig.pageable.skip !== 1) {
        const previousPage = transactionsConfig.pageable.skip - 1 || 1;
        dispatch(updateActivePage(previousPage));
      } else {
        // Initialize/set the state of the transactions to use
        dispatch({
          type: actionTypes.GET_TRANSACTION_LIST,
          payload: {
            transactions: response.data.result,
            total: response.data.total,
            isLoading: false,
          },
        });
      }

      if (!_.isEmpty(postTxLoadMessage) && postTxLoadMessage != null) {
        dispatch(showToastNotification(ToastType.Success, postTxLoadMessage));
      }
    } catch (e) {
      dispatch(
        showToastNotification(ToastType.Danger, i18n.t('transactions:actions.failedToLoadTxns'), getErrorMessage(e)),
      );
      dispatch({
        type: actionTypes.GET_TRANSACTION_LIST,
        payload: {
          transactions: [],
          total: 0,
        },
      });
    }
  };
};

export const getAllTransactionsThunk = (serviceId: number, oldService: string, newService: string) => {
  return async (dispatch: any) => {
    const url = `/workspaces/{workspaceId}/transactions?serviceId=${serviceId}&sort=-id&limit=-1`;
    try {
      // Make call to fetch all transactions from the API
      const mockServiceTransactions = [];
      const response = await axios.get(url);

      if (response && response.data && response.data.result) {
        const txs: Transaction[] = response.data.result;

        for (let i = 0; i < txs.length; i++) {
          if (txs[i].id) {
            mockServiceTransactions.push({ txnId: txs[i].id, priority: txs[i].priority });
          }
        }
        dispatch(createMockServiceThunk(TRANSACTIONAL, mockServiceTransactions, oldService, newService));
      }
    } catch (e) {}
  };
};

export const getAllTransactionsAndCreateZipFileThunk = (service: Service) => {
  return async (dispatch: any) => {
    //show info toast
    const infoToast = dispatch(
      showToastNotification(
        ToastType.Info,
        '',
        formattedTextThreeArgs(i18n.t('infoMsgs.service'), i18n.t('infoMsgs.isBeingExported'), service.name),
        0,
      ),
    );
    try {
      const serviceId = service.id;
      const workspaceId = getActiveWorkspaceId();
      const url = `/api/v1/workspaces/${workspaceId}/services/${serviceId}/export?withData=true`;
      const fileName = service.name + ' (' + generateDate() + ')' + '.zip';

      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);

      link.click();
      link.remove();

      await dispatch(hideToast(infoToast));

      //Hide info toast before success toast
      await dispatch(hideToast(infoToast));
      await dispatch(
        showToastNotification(
          ToastType.Success,
          '',
          formattedTextThreeArgs(i18n.t('infoMsgs.service'), i18n.t('successMsgs.exportServiceMsg'), service.name),
        ),
      );
    } catch (error) {
      await dispatch(hideToast(infoToast));
      const errorMsg = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, `Could not export service ${service.name}`, errorMsg));
    }
  };
};

export const cloneTransactionId = (transaction: Transaction): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>) => {
    MixpanelService.track(CLONE_TRANSACTION());

    const transactionToClone = { ...transaction };
    transactionToClone.name = i18n.t('transactions:actions.clonedPostfix', {
      name: transactionToClone.name,
      date: generateDate(),
    });

    try {
      await axios.post(`/workspaces/{workspaceId}/transactions?serviceId=${transactionToClone.serviceId}`, {
        transactions: [transactionToClone],
      });
      dispatch(
        showToastNotification(
          ToastType.Success,
          `${i18n.t('transactions:actions.successfullyDuplicatedTxn')} ${transaction.name}`,
        ),
      );
      return dispatch(getTransactions());
    } catch (e) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.unexpectedError'),
          i18n.t('transactions:actions.failedToDuplicateTxn', {
            reason: e.response ? e.response.data.error : 'Something went wrong.',
          }),
        ),
      );
    }
  };
};

export const deleteTransactionById = (
  transaction: Transaction,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>) => {
    MixpanelService.track(DELETE_TRANSACTION());

    try {
      await axios.delete(`/workspaces/{workspaceId}/transactions/${transaction.id}`);
      dispatch(
        showToastNotification(
          ToastType.Success,
          `${i18n.t('transactions:actions.successfullyDeletedTxn')} ${transaction.name}`,
        ),
      );
      //Clear txn selection in redux store
      dispatch(selectTransactions([]));

      return dispatch(getTransactions());
    } catch (e) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('transactions:actions.failedToDeleteTxn')} ${transaction.name}`,
        ),
      );
    }
  };
};

export const openHttpTransactionEditorAction = (type: TransactionType) =>
  action(actionTypes.OPEN_HTTP_TRANSACTION_EDITOR_FOR_CREATE, { transactionType: type });
export const openMsgTransactionEditorAction = () => action(actionTypes.OPEN_MSG_TRANSACTION_EDITOR_FOR_CREATE, {});
export const closeTransactionEditorAction = () => action(actionTypes.CLOSE_TRANSACTION_EDITOR_FOR_CREATE, {});
export const activePageUpdated = (activePage: number) => action(actionTypes.UPDATE_ACTIVE_PAGE, activePage);
export const changeTransactionPageSize = (activePage: number) =>
  action(actionTypes.SET_TRANSACTION_PAGE_SIZE, activePage);

/**
 * Dispatches open transaction editor action and call mix panel.
 */
export const openTransactionEditorThunk = (
  txType: TransactionType,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>): Promise<void> => {
    // Update the pagination in the store
    if (txType === 'HTTP') {
      dispatch(openHttpTransactionEditorAction(txType));
    } else {
      dispatch(openMsgTransactionEditorAction());
    }

    MixpanelService.track(CREATE_BLANK_TRANSACTION());
  };
};

/**
 * Dispatches close transaction editor action and call mix panel.
 */
export const closeAndDeleteBlankTransactionThunk = (): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>): Promise<void> => {
    // Update the pagination in the store
    dispatch(closeTransactionEditorAction());
    MixpanelService.track(CLOSE_BLANK_TRANSACTION());
  };
};

/**
 * Updates pageable in the store and triggers refresh of the table.
 * @param activePage
 */
export const updateActivePage = (activePage: number): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>): Promise<void> => {
    // Update the pagination in the store
    dispatch(activePageUpdated(activePage));
    // Trigger refresh of transactions after update
    dispatch(getTransactions());
  };
};

export const putTransaction = (transactionWrapper: TransactionWrapper) => {
  return axios.put<BlazeMeterApiResponse>(
    `workspaces/{workspaceId}/transactions/${transactionWrapper.transaction.id}`,
    transactionWrapper.transaction,
    { params: { serviceId: transactionWrapper.serviceId } },
  );
};

export const postTransaction = (transactionWrapper: TransactionWrapper) => {
  const transactionDto: TransactionTransferObjectWrapper = {
    transactions: [transactionWrapper.transaction],
  };
  return axios.post<BlazeMeterApiResponse>(`workspaces/{workspaceId}/transactions`, transactionDto, {
    params: {
      serviceId: transactionWrapper.serviceId,
    },
  });
};

export const updateTransactionAction = (
  transactionWrapper: TransactionWrapper,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>): Promise<void> => {
    const isBodyMagicStringInvalid = false;
    const isHeaderMagicStringInvalid = false;
    const resBodyInvalidMagicStrings = '';
    const resHeaderInvalidMagicStrings = '';
    try {
      const response = await putTransaction(transactionWrapper);
      const transaction = response.data.result;

      if (!isPrivateCloud()) {
        postValidateTransactionForBlazeData(transaction);
      }

      dispatch(
        showToastNotification(
          ToastType.Success,
          `${i18n.t('transactions:actions.successfullyUpdatedTxn')} ${transaction.name}`,
        ),
      );
      dispatch({
        type: actionTypes.UPDATE_TRANSACTION,
        payload: {
          isResponseBodyMagicStringInvalid: false,
          isResponseHeaderMagicStringInvalid: false,
          expandedTransactionId: transactionWrapper.transaction.id,
          resBodyInvalidMagicStrings: '',
          resHeaderInvalidMagicStrings: '',
        },
      });
      dispatch(getTransactions());
    } catch (e) {
      if (e.response && _.has(e.response, 'data.error')) {
        const error = _.get(e, 'response.data.error', null);
        if (
          error &&
          (e.response.data.error.includes('enough valid bits') || e.response.data.error.includes('Illegal base64'))
        ) {
          const errorMsg = 'Invalid Base64 encoded binary content.';
          dispatch(
            showToastNotification(
              ToastType.Danger,
              `${i18n.t('transactions:actions.cantUpdateTxn')} ${transactionWrapper.transaction.name}. ${errorMsg}`,
            ),
          );
        } else {
          dispatch(
            processMagicStringValidationThunk(
              e,
              transactionWrapper.transaction.id,
              transactionWrapper.transaction.name,
              isBodyMagicStringInvalid,
              isHeaderMagicStringInvalid,
              resBodyInvalidMagicStrings,
              resHeaderInvalidMagicStrings,
            ),
          );
        }
      }
      MixpanelService.track(UPDATE_TRANSACTION());
    }
  };
};

export const createTransactionAction = (
  transactionWrapper: TransactionWrapper,
  selectedEditorTab?: EditorTabType,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>) => {
    const isBody = false;
    const isHeader = false;
    const resBody = '';
    const resHeader = '';
    try {
      const transactionDto: TransactionTransferObjectWrapper = {
        transactions: [transactionWrapper.transaction],
      };
      const response = await axios.post<BlazeMeterApiResponse>(
        `workspaces/{workspaceId}/transactions`,
        transactionDto,
        {
          params: {
            serviceId: transactionWrapper.serviceId,
          },
        },
      );
      const transaction = response.data.result[0];
      transactionWrapper.transaction.id = transaction.id;
      dispatch(
        showToastNotification(
          ToastType.Success,
          `${i18n.t('transactions:actions.successfullyCreatedTxn')} ${transaction.name}`,
        ),
      );

      // Post validation of txn detail for blaze data
      if (!isPrivateCloud()) {
        postValidateTransactionForBlazeData(transaction);
      }

      //Refresh txn list
      await dispatch(getTransactions());

      // Close txn editor after refresh took place, it creates more smooth user experience
      dispatch(closeTransactionEditorAction());

      //Update txn
      dispatch({
        type: actionTypes.UPDATE_TRANSACTION,
        payload: {
          isResponseBodyMagicStringInvalid: false,
          isResponseHeaderMagicStringInvalid: false,
          expandedTransactionId: transaction.id,
          resBodyInvalidMagicStrings: '',
          resHeaderInvalidMagicStrings: '',
        },
      });

      //Save preselected editor tab
      if (selectedEditorTab) {
        await dispatch(editorTabChanged(selectedEditorTab));
      }
    } catch (e) {
      console.debug('Failed to create transaction.');
      if (e.response) {
        const error = _.get(e, 'response.data.error', null);
        if (
          error &&
          (e.response.data.error.includes('enough valid bits') || e.response.data.error.includes('Illegal base64'))
        ) {
          const errorMsg = 'Invalid Base64 encoded binary content.';
          dispatch(
            showToastNotification(
              ToastType.Danger,
              `${i18n.t('transactions:actions.failedToCreateTxn')} ${transactionWrapper.transaction.name}. ${errorMsg}`,
            ),
          );
        } else {
          dispatch(
            processMagicStringValidationThunk(
              e,
              transactionWrapper.transaction.id,
              transactionWrapper.transaction.name,
              isBody,
              isHeader,
              resBody,
              resHeader,
            ),
          );
        }
      }
    }
    MixpanelService.track(CREATE_TRANSACTION());
  };
};

/**
 * Updates transaction field inside of template / mock service.
 */
export const updateMockServiceTransactionField = (
  fieldName: string,
  fieldValue: any,
  transactionId: number,
  context: MockService | MockServiceTemplate,
  transactionType: TransactionType,
) => {
  if (!isTemplate(context)) {
    patchMockServiceTransaction(context as MockService, transactionId, fieldName, fieldValue, transactionType);
  } else {
    patchTemplateTransaction(transactionId, context as MockServiceTemplate, { [fieldName]: fieldValue });
  }
};

export const patchMockServiceTransaction = async (
  mockService: MockService,
  transactionId: number,
  fieldName: string,
  fieldValue: any,
  transactionType: TransactionType,
) => {
  let ms = { ...mockService } as MockService;

  //Create a draft beforehand if this object is not a draft
  let reload = false;
  if (!ms.isDraft) {
    ms = await store.dispatch(createDraft(ms));
    reload = true;
  }

  //Update Store
  store.dispatch(updateMockServiceTransactionAction(transactionId, fieldName, fieldValue));

  await acApi.mockServiceDrafts.updateTransaction(ms.id, transactionId, {
    [fieldName]: fieldValue,
  });

  // Reload shuttle with transactions
  if (reload) {
    store.dispatch(reloadTransactionShuttle(ms, transactionType));
  }
};

/**
 * Action which updates transaction priority either in Virtual Service or Virtual Service Template on backend.
 * Returns axios promise.
 */
export const updateTransactionPriorityAction = (
  priorityValue: number,
  transactionId: number,
  context: MockService | MockServiceTemplate,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async () => {
    // Virtual Service type
    if (!isTemplate(context)) {
      await patchMockServiceTransactionPriority(priorityValue, transactionId, context as MockService);
    }
    // Template
    else {
      await updateTemplatePriority(priorityValue, transactionId, context as MockServiceTemplate);
    }
  };
};

const createTemplateDraft = async (template: MockServiceTemplate) => {
  const draft = await store.dispatch(createTemplateDraft(template) as any);
  const id = template.id;
  if (!draft) {
    return;
  }
  template.isDraft = true;
  template.id = draft.id;
  template.serviceMockTemplateId = id;
  store.dispatch(swapMockServiceTemplateAction(id, template));
  return template;
};

const updateTemplatePriority = async (priorityValue: number, transactionId: number, context: MockServiceTemplate) => {
  let template = { ...context } as MockServiceTemplate;
  if (!template.isDraft) {
    template = await createTemplateDraft(template);
  }

  await acApi.templateDrafts.updateTransaction(template.id, transactionId, {
    priority: priorityValue,
  });

  // Reload shuttle with transactions
  const activeRunner = (store.getState() as ApplicationState).mockServiceTemplateDetail.runnerTab;
  store.dispatch(reloadTransactionsForTemplate(template, activeRunner === 'httpRunner' ? 'HTTP' : 'MESSAGING') as any);
};

const patchTemplateTransaction = async (transactionId: number, context: MockServiceTemplate, update: any) => {
  let template = { ...context } as MockServiceTemplate;
  if (!template.isDraft) {
    template = await createTemplateDraft(template);
  }

  await acApi.templateDrafts.updateTransaction(template.id, transactionId, update);

  // Reload shuttle with transactions
  const activeRunner = (store.getState() as ApplicationState).mockServiceTemplateDetail.runnerTab;
  store.dispatch(reloadTransactionsForTemplate(template, activeRunner === 'httpRunner' ? 'HTTP' : 'MESSAGING') as any);
};

const patchMockServiceTransactionPriority = async (
  priorityValue: number,
  transactionId: number,
  context: MockService,
) => {
  let id = context.id;

  const mockService = { ...context } as MockService;
  // Update the transaction priority immediately in the store. We need it because
  // otherwise we can see old value before draft is created
  store.dispatch(updateTransactionPriority(transactionId, priorityValue));

  //Create draft beforehand if this object is not a draft
  if (!mockService.isDraft) {
    await store.dispatch(createDraft(mockService));
    id = mockService.id;
  }

  await acApi.mockServiceDrafts.updateTransaction(id, transactionId, {
    priority: priorityValue,
  });

  // Reload shuttle with transactions
  store.dispatch(reloadTransactionShuttle(mockService));
};

// const patchPriority = async (patchUrl: string, priorityValue: number) => {
//   try {
//     await axios.patch(patchUrl, {
//       priority: priorityValue,
//     });
//   } catch (error) {}
// };

export const expandTransactionRowAction = (transactionId?: number | null) =>
  action(actionTypes.EXPAND_TRANSACTION_ROW, transactionId);

function getCombinedMessage(
  isBodyMagicStringInvalid: boolean,
  isHeaderMagicStringInvalid: boolean,
  resBodyInvalidMagicStrings: string,
  resHeaderInvalidMagicStrings: string,
) {
  let combinedMsg = '';
  if (isBodyMagicStringInvalid && isHeaderMagicStringInvalid) {
    combinedMsg =
      `${i18n.t('transactions:actions.incorrect')}` +
      resBodyInvalidMagicStrings +
      ',' +
      resHeaderInvalidMagicStrings +
      `${i18n.t('transactions:actions.syntaxErr')}`;
  } else if (isBodyMagicStringInvalid) {
    combinedMsg =
      `${i18n.t('transactions:actions.incorrect')}` +
      resBodyInvalidMagicStrings +
      `${i18n.t('transactions:actions.syntaxBodyErr')}`;
  } else if (isHeaderMagicStringInvalid) {
    combinedMsg =
      `${i18n.t('transactions:actions.incorrect')}` +
      resHeaderInvalidMagicStrings +
      `${i18n.t('transactions:actions.syntaxHeaderErr')}`;
  }
  return combinedMsg;
}

export const magicStringHelper = (
  id: number | undefined,
  isBodyMagicStringInvalid: boolean,
  isHeaderMagicStringInvalid: boolean,
  resBodyInvalidMagicStrings: string,
  resHeaderInvalidMagicStrings: string,
) =>
  action(actionTypes.UPDATE_TRANSACTION, {
    isResponseBodyMagicStringInvalid: isBodyMagicStringInvalid,
    isResponseHeaderMagicStringInvalid: isHeaderMagicStringInvalid,
    expandedTransactionId: id,
    resBodyInvalidMagicStrings: resBodyInvalidMagicStrings,
    resHeaderInvalidMagicStrings: resHeaderInvalidMagicStrings,
  });

export const processMagicStringValidationThunk = (
  e: any,
  id: number | undefined,
  name: string,
  isBody: boolean,
  isHeader: boolean,
  resBody: string,
  resHeader: string,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>) => {
    let errorMsg = 'Something went wrong';
    let combinedMsg = '';
    let i = 0;
    if (e.response.data.error === 'Syntax is incorrect.') {
      if (e.response.data.result.errorFields) {
        e.response.data.result.errorFields.forEach(function(errorFields: ErrorFields) {
          if (errorFields.fieldType === 'RESPONSE_BODY') {
            isBody = true;
            resBody = resBody + ' ' + errorFields.values;
          } else if (errorFields.fieldType === 'RESPONSE_HEADER_VALUE') {
            isHeader = true;
            //to skit , separator for first one and add it for remaining strings
            i++;
            if (i === 1) {
              resHeader = resHeader + errorFields.values;
            } else {
              resHeader = resHeader + ',' + errorFields.values;
            }
          }
        });
      }
      if (isBody || isHeader) {
        dispatch(magicStringHelper(id, isBody, isHeader, resBody, resHeader));
        //prepare separate toast messages for body and header
        combinedMsg = getCombinedMessage(isBody, isHeader, resBody, resHeader);
      }
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('transactions:actions.cantUpdateTxn')} ${name}. ${combinedMsg}`,
        ),
      );
    } else {
      errorMsg = e.response.data.error;
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('transactions:actions.cantUpdateTxn')} ${name}. ${errorMsg}.`,
        ),
      );
    }
  };
};

export const getTransaction = (transactionId: number | string) => {
  return acApi.transactions.getTransaction(transactionId);
};
